import React, { HTMLAttributes } from 'react';
import { classNames } from '@/utils/classNames';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined' | 'interactive';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const Card: React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>;
  Content: React.FC<CardContentProps>;
  Footer: React.FC<CardFooterProps>;
} = ({ variant = 'default', padding = 'md', className, children, ...props }) => {
  const baseClasses = 'bg-white dark:bg-gray-800 rounded-lg fade-in';

  const variantClasses = {
    default: 'shadow-sm border border-gray-200 dark:border-gray-700',
    elevated: 'shadow-lg border border-gray-200 dark:border-gray-700',
    outlined: 'border-2 border-gray-200 dark:border-gray-700',
    interactive:
      'shadow-sm border border-gray-200 dark:border-gray-700 card-hover cursor-pointer touch-target',
  };

  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const cardClasses = classNames(
    baseClasses,
    variantClasses[variant],
    paddingClasses[padding],
    className
  );

  return (
    <div className={cardClasses} {...props}>
      {children}
    </div>
  );
};

const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  className,
  children,
  ...props
}) => {
  const headerClasses = classNames('flex items-start justify-between', className);

  return (
    <div className={headerClasses} {...props}>
      <div className='min-w-0 flex-1'>
        {title && (
          <h3 className='text-lg font-medium text-gray-900 dark:text-white truncate'>{title}</h3>
        )}
        {subtitle && <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>{subtitle}</p>}
        {children}
      </div>
      {action && <div className='ml-4 flex-shrink-0'>{action}</div>}
    </div>
  );
};

const CardContent: React.FC<CardContentProps> = ({ className, children, ...props }) => {
  const contentClasses = classNames('text-gray-700 dark:text-gray-300', className);

  return (
    <div className={contentClasses} {...props}>
      {children}
    </div>
  );
};

const CardFooter: React.FC<CardFooterProps> = ({ className, children, ...props }) => {
  const footerClasses = classNames('flex items-center justify-between', className);

  return (
    <div className={footerClasses} {...props}>
      {children}
    </div>
  );
};

Card.Header = CardHeader;
Card.Content = CardContent;
Card.Footer = CardFooter;

export default Card;
